import { useState } from 'react';
import { useCompany } from '../contexts/CompanyContext';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Building2, Plus, Edit2, Trash2, Users, MapPin, Phone, Globe, Hash } from 'lucide-react';
import { toast } from 'sonner';

const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

const defaultForm = { name: '', ein: '', address: '', phone: '', website: '' };

export default function CompaniesPage() {
  const { companies, fetchCompanies } = useCompany();
  const { apiCall } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [empCounts, setEmpCounts] = useState({});

  // Fetch employee counts per company
  useState(() => {
    (async () => {
      try {
        const res = await apiCall('get', '/employees?limit=5000');
        const counts = {};
        (res.data.employees || []).forEach(emp => {
          const cid = emp.company_id || 'unassigned';
          counts[cid] = (counts[cid] || 0) + 1;
        });
        setEmpCounts(counts);
      } catch {}
    })();
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await apiCall('put', `/companies/${editing}`, form);
        toast.success('Company updated');
      } else {
        await apiCall('post', '/companies', form);
        toast.success('Company created');
      }
      setShowDialog(false);
      setForm(defaultForm);
      setEditing(null);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (company) => {
    setForm({ name: company.name, ein: company.ein || '', address: company.address || '', phone: company.phone || '', website: company.website || '' });
    setEditing(company.id);
    setShowDialog(true);
  };

  const handleDelete = async (company) => {
    setDeleteTarget(company);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiCall('delete', `/companies/${deleteTarget.id}`);
      toast.success('Company deleted');
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div data-testid="companies-page">
      <motion.div {...fadeIn} transition={{ duration: 0.3 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight mb-1">Companies</h1>
            <p className="text-[#64748B]">Manage multiple companies and EINs under your account</p>
          </div>
          <Button
            data-testid="add-company-btn"
            onClick={() => { setForm(defaultForm); setEditing(null); setShowDialog(true); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 font-semibold shadow-md hover:shadow-lg hover:shadow-indigo-500/20 transition-all active:scale-[0.98]"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Company
          </Button>
        </div>
      </motion.div>

      {companies.length === 0 ? (
        <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.1 }} className="bg-white rounded-2xl border border-indigo-100/50 p-12 text-center shadow-sm">
          <Building2 className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="text-lg font-bold text-[#0F172A] mb-2">No companies yet</h3>
          <p className="text-sm text-[#64748B] mb-6 max-w-md mx-auto">Add your first company to start managing employees and PAF files. You can add multiple companies to manage different EINs.</p>
          <Button
            data-testid="add-first-company-btn"
            onClick={() => { setForm(defaultForm); setEditing(null); setShowDialog(true); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 font-semibold shadow-md"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Your First Company
          </Button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {companies.map((company, i) => (
            <motion.div
              key={company.id}
              {...fadeIn}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="bg-white rounded-2xl border border-indigo-100/50 p-6 shadow-sm hover:shadow-md transition-all"
              data-testid={`company-card-${company.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(company)}
                    className="p-2 rounded-lg hover:bg-indigo-50 text-[#94A3B8] hover:text-indigo-600 transition-colors"
                    data-testid={`edit-company-${company.id}`}
                  >
                    <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => handleDelete(company)}
                    className="p-2 rounded-lg hover:bg-red-50 text-[#94A3B8] hover:text-red-500 transition-colors"
                    data-testid={`delete-company-${company.id}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
              <h3 className="text-lg font-bold text-[#0F172A] mb-1 tracking-tight">{company.name}</h3>
              {company.ein && (
                <p className="text-sm text-[#64748B] flex items-center gap-1.5 mb-3">
                  <Hash className="w-3.5 h-3.5" /> EIN: {company.ein}
                </p>
              )}
              <div className="space-y-1.5 text-xs text-[#94A3B8]">
                {company.address && (
                  <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {company.address}</p>
                )}
                {company.phone && (
                  <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {company.phone}</p>
                )}
                {company.website && (
                  <p className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> {company.website}</p>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-indigo-50">
                <p className="text-xs font-semibold text-indigo-600 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> {empCounts[company.id] || 0} employee(s)
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg bg-white rounded-2xl" data-testid="company-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A] tracking-tight">
              {editing ? 'Edit Company' : 'Add Company'}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">
              {editing ? 'Update company details' : 'Add a new company to manage under your account'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Company Name *</Label>
              <Input data-testid="company-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Acme Technologies Inc." className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">EIN (Employer Identification Number)</Label>
              <Input data-testid="company-ein-input" value={form.ein} onChange={e => setForm({...form, ein: e.target.value})} placeholder="e.g. 12-3456789" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Address</Label>
              <Input data-testid="company-address-input" value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="123 Main St, City, State" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Phone</Label>
                <Input data-testid="company-phone-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(555) 123-4567" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Website</Label>
                <Input data-testid="company-website-input" value={form.website} onChange={e => setForm({...form, website: e.target.value})} placeholder="www.example.com" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl px-6 border-[#E2E8F0]" data-testid="cancel-company-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.name.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 shadow-md hover:shadow-lg hover:shadow-indigo-500/20" data-testid="save-company-btn"
            >
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm bg-white rounded-2xl" data-testid="delete-company-dialog">
          <div className="text-center pt-2">
            <div className="flex items-center justify-center gap-1.5 mb-5">
              <img src="/logo.svg" alt="PAFHub" className="w-7 h-7" />
              <span className="font-bold text-sm tracking-tight"><span className="text-[#000000]">PAF</span><span className="text-[#4F46E5]">Hub</span></span>
            </div>
            <div className="w-14 h-14 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-500" strokeWidth={1.5} />
            </div>
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-[#0F172A] tracking-tight">Delete Company</DialogTitle>
              <DialogDescription className="text-sm text-[#64748B] mt-2">
                Are you sure you want to delete <span className="font-semibold text-[#0F172A]">"{deleteTarget?.name}"</span>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                className="flex-1 rounded-xl border-[#E2E8F0] h-11"
                data-testid="cancel-delete-company-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white h-11"
                data-testid="confirm-delete-company-btn"
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
